import { useCallback, useEffect, useRef } from 'react';

// Commit-on-blur fields lose their draft when the component unmounts without
// a blur event (programmatic drawer close, route change): browsers don't fire
// focusout when a focused element is removed from the DOM. This hook tracks
// the in-progress draft and flushes it on unmount — safe because React Query
// runs mutations through the mutation cache, so the request and the
// hook-level cache updates complete even after the component is gone. On hard
// page unloads no authenticated request can be sent, so it warns instead.
export function useCommittableDraft(
  committedValue: string,
  commit: (value: string) => void,
) {
  const draftRef = useRef<string | null>(null);

  const lastCommittedValueRef = useRef(committedValue);
  if (lastCommittedValueRef.current !== committedValue) {
    lastCommittedValueRef.current = committedValue;
    draftRef.current = null;
  }

  const commitRef = useRef(commit);
  commitRef.current = commit;

  const setDraft = useCallback((value: string) => {
    draftRef.current = value;
  }, []);

  const commitDraft = useCallback((value: string) => {
    draftRef.current = null;
    commitRef.current(value);
  }, []);

  useEffect(() => {
    const getPendingDraft = () => {
      if (draftRef.current === null) return null;
      if (draftRef.current === lastCommittedValueRef.current) return null;
      return draftRef.current;
    };

    const handleBeforeUnload = (ev: BeforeUnloadEvent) => {
      if (getPendingDraft() === null) return;
      ev.preventDefault();
      // Chromium only shows the confirmation prompt if returnValue is set
      ev.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      const pendingDraft = getPendingDraft();
      if (pendingDraft !== null) {
        commitRef.current(pendingDraft);
      }
    };
  }, []);

  return { setDraft, commitDraft };
}
